import { ListPagination, TableNoDataRow } from '@/common/components'
import { LoadingPanel } from '@/common/components/LoadingPanel/LoadingPanel'
import { loadViews, usePagedListState } from '@/hooks'
import type { MenusNavItem } from '@/common/types'
import CustomPalette from '@/theme/CustomPalette'
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { useEffect, useState } from 'react'
import { useCourseSearchFormContext } from '../../hooks/useCourseSearchFormContext'
import { useSearchCourses } from '../../hooks/useSearchCourses'
import { Course } from '../../types'
import { CourseListItem } from '../CourseListItem/CourseListItem'

export type CourseListProps = {
  routeLabel: string
  titles: string[]
  emptyList: string
  loadingLabel: string
  menus?: MenusNavItem[]
  openAction: (courseCode: string) => void
  closeAction?: () => void
}

export const CourseList = (props: CourseListProps): JSX.Element => {
  const { menus, closeAction } = props
  const { values: searchValues } = useCourseSearchFormContext()
  const { page, size, onPageChange, onSizeChange, keepPageInRange } = usePagedListState({
    initialFilters: {},
    outsideFilters: { name: searchValues.name },
  })
  const { data: list = { items: [], total: 0 }, isFetching, isUninitialized } = useSearchCourses(page, size)

  const isLoading = isFetching || isUninitialized
  const pageCount = Math.ceil((list.total ?? 0) / size)
  if (!isLoading) keepPageInRange(pageCount)

  const [menuViews, setMenuViews] = useState<Array<JSX.Element>>([])

  useEffect(() => {
    let isCurrent = true
    loadViews({ views: menus, action: closeAction }).then((views) => {
      if (isCurrent) setMenuViews(views)
    })
    return () => {
      isCurrent = false
    }
  }, [menus, closeAction])

  const [delayedLoading, setDelayedLoading] = useState(true)

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null

    if (isLoading) {
      setDelayedLoading(true)
    } else {
      timer = setTimeout(() => {
        setDelayedLoading(false)
      }, 500)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [isLoading])

  return (
    <Box sx={{ pb: 5 }}>
      <LoadingPanel loading={delayedLoading} label={props.loadingLabel}>
        <Box sx={{ minWidth: 1050 }}>
          <TableContainer sx={{ height: 600 }}>
            <Table sx={{ minWidth: 750, height: 'max-content' }}>
              <TableHead sx={{ backgroundColor: CustomPalette.SecondaryCloud }}>
                <TableRow sx={{ '& .MuiTableCell-root': { paddingLeft: 0, border: 'none' }, height: '48px' }}>
                  <TableCell sx={{ visibility: 'none', width: 0 }}></TableCell>
                  {props.titles?.map((title) => (
                    <TableCell key={title.toString()}>{title}</TableCell>
                  ))}
                  <TableCell sx={{ visibility: 'none', width: 0 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!list.items.length ? (
                  <TableNoDataRow colSpan={props.titles.length + 2} message={props.emptyList} />
                ) : (
                  list?.items.map((course: Course) => (
                    <CourseListItem
                      key={course.id}
                      data={course}
                      objectType={course.code ? `${course.code}` : `${course.id}`}
                      routeLabel={props.routeLabel}
                      openUpdateMenu={props.openAction}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {menuViews}
        <Box sx={{ mt: 2, px: 4 }}>
          <ListPagination
            page={page}
            pageCount={pageCount}
            onPageChange={onPageChange}
            pageSize={size}
            onPageSizeChange={onSizeChange}
          />
        </Box>
      </LoadingPanel>
    </Box>
  )
}
